import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  RefreshCw,
  Package,
  Filter,
  X,
  Grid3x3,
  LayoutGrid,
  List,
  Grid2x2,
  Image as ImageIcon,
  Building2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { getProducts, getProductStatistics, deleteProduct } from '../api/products';
import { getBrands, getSizes, getUseCases, getTileTypes } from '../api/categories';
import { getBranches, getBranchProducts } from '../api/branches';
import { getProductImage } from '../utils/imageUtils';
import BlurredValue from '../components/common/BlurredValue';
import Pagination from '../components/common/Pagination';

const ProductsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);

  // Categories for filters
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);

  // Branch filtering
  const { isMultiBranchMode } = useBranch();
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  // Filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: '',
    productType: '', // 'tile', 'accessory', or '' for all
    brandId: '',
    sizeId: '',
    useCaseId: '',
    typeId: '',
    isActive: '',
    isFeatured: '',
    inStock: false,
    lowStockOnly: false,
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const [showFilters, setShowFilters] = useState(false);

  // View mode state
  const [viewMode, setViewMode] = useState(() => {
    // Get saved view mode from localStorage or default to 'grid'
    return localStorage.getItem('productViewMode') || 'grid';
  });

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('productViewMode', viewMode);
  }, [viewMode]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [brandsData, sizesData, useCasesData, tileTypesData] = await Promise.all([
          getBrands(),
          getSizes(),
          getUseCases(),
          getTileTypes()
        ]);
        setBrands(brandsData);
        setSizes(sizesData);
        setUseCases(useCasesData);
        setTileTypes(tileTypesData);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch branches for multi-branch mode
  useEffect(() => {
    const fetchBranchesData = async () => {
      if (!isMultiBranchMode) return;
      try {
        const data = await getBranches();
        setBranches(data.branches || data || []);
      } catch (error) {
        console.error('Failed to load branches:', error);
      }
    };
    fetchBranchesData();
  }, [isMultiBranchMode]);

  // Handle URL parameters (e.g., from LowStockAlert)
  useEffect(() => {
    const lowStockParam = searchParams.get('lowStockOnly');
    if (lowStockParam === 'true') {
      setFilters((prev) => ({
        ...prev,
        lowStockOnly: true,
        page: 1
      }));
      setShowFilters(true); // Show filters to make it clear what's applied
    }
  }, [searchParams]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let response;

      // If a branch is selected, fetch branch-specific products
      if (selectedBranchId) {
        const branchProducts = await getBranchProducts(selectedBranchId);
        // Transform branch products to match expected format
        const products = (branchProducts || []).map((bp) => ({
          ...bp,
          id: bp.product_id,
          // Use branch price if set, otherwise global price
          selling_price: bp.branch_price || bp.selling_price,
          stock_quantity: bp.branch_stock || bp.stock_quantity
        }));

        // Apply client-side filtering for branch products
        let filteredProducts = products;
        if (filters.search) {
          const search = filters.search.toLowerCase();
          filteredProducts = filteredProducts.filter(
            (p) =>
              p.name?.toLowerCase().includes(search) ||
              p.product_code?.toLowerCase().includes(search)
          );
        }
        if (filters.brandId) {
          filteredProducts = filteredProducts.filter((p) => p.brand_id == filters.brandId);
        }
        if (filters.lowStockOnly) {
          filteredProducts = filteredProducts.filter(
            (p) => p.stock_quantity <= (p.min_stock_level || 5)
          );
        }

        setProducts(filteredProducts);
        setPagination({
          page: 1,
          limit: filteredProducts.length,
          total: filteredProducts.length,
          totalPages: 1,
          hasMore: false
        });
      } else {
        // Normal global product fetch
        response = await getProducts(filters);
        // Normalize stock_quantity: use global_stock (sum of all branch stocks) if available
        const normalizedProducts = (response.products || []).map((p) => ({
          ...p,
          // In multi-branch mode, global_stock contains the actual total stock
          stock_quantity: p.global_stock !== undefined ? p.global_stock : p.stock_quantity
        }));
        setProducts(normalizedProducts);
        setPagination(response.pagination);
      }
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [filters, selectedBranchId]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await getProductStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchStatistics();
  }, [fetchProducts, fetchStatistics]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Only reset to page 1 when changing filters OTHER than page
      ...(key !== 'page' && { page: 1 })
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 12,
      search: '',
      productType: '',
      brandId: '',
      sizeId: '',
      useCaseId: '',
      typeId: '',
      isActive: '',
      isFeatured: '',
      inStock: false,
      lowStockOnly: false,
      sortBy: 'created_at',
      sortOrder: 'DESC'
    });
  };

  // Handle delete with optimistic UI
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await deleteProduct(id);
      // Optimistically remove from local state instantly
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success('Product deleted successfully');
      // Silently refresh statistics in background (no loading flash)
      fetchStatistics();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete product';
      toast.error(errorMessage);
    }
  };

  // Check if user can edit
  const canEdit = ['admin', 'super_admin', 'retail_admin', 'branch_admin', 'system_admin'].includes(
    user?.role
  );

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  // Render product type badge
  const renderProductTypeBadge = (productType) => {
    if (productType === 'accessory') {
      return (
        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-700 border border-purple-300">
          ACC
        </span>
      );
    }
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-700 border border-blue-300">
        TILE
      </span>
    );
  };

  // Get category display text based on product type
  const getCategoryDisplay = (product) => {
    if (product.product_type === 'accessory') {
      return product.accessory_category_name || 'Accessory';
    }
    return product.brand_name || 'N/A';
  };

  // Render products based on view mode
  const renderProducts = () => {
    switch (viewMode) {
      case 'list':
        return renderListView();
      case 'compact':
        return renderCompactView();
      case 'grid':
        return renderGridView();
      case 'detailed':
        return renderDetailedView();
      default:
        return renderGridView();
    }
  };

  // List View - Table-like layout with mobile card view
  const renderListView = () => (
    <>
      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3">
        {products.map((product) => (
          <div
            key={product.id}
            className={`rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow ${
              product.stock_quantity <= product.min_stock_level
                ? 'bg-red-50 border-2 border-red-300'
                : 'bg-white'
            }`}
          >
            <div onClick={() => navigate(`/products/${product.id}`)}>
              <div className="flex items-start gap-3 mb-3">
                <div className="h-12 w-12 flex-shrink-0 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                  {getProductImage(product.images) ? (
                    <img
                      src={getProductImage(product.images)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                    {product.name}
                  </h3>
                  <div className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black inline-block">
                    {product.product_code}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Brand</p>
                  <p className="text-sm font-medium text-gray-900">{product.brand_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Size</p>
                  <p className="text-sm font-medium text-gray-900">{product.size_dimension}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Price</p>
                  <p className="text-sm font-bold text-accent-gold">
                    {formatCurrency(product.selling_price)}
                  </p>
                </div>
                {canEdit && (
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Stock</p>
                    <p
                      className={`text-sm font-bold ${
                        product.stock_quantity > product.min_stock_level
                          ? 'text-green-600'
                          : product.stock_quantity > 0
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {product.stock_quantity} {product.unit}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {canEdit && (
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/products/${product.id}/edit`);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                {canEdit && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                )}
                {canEdit && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr
                  key={product.id}
                  onClick={() => navigate(`/products/${product.id}`)}
                  className={`cursor-pointer transition-colors ${
                    product.stock_quantity <= product.min_stock_level
                      ? 'bg-red-50 hover:bg-red-100'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0 bg-gray-200 rounded flex items-center justify-center mr-3 overflow-hidden">
                        {getProductImage(product.images) ? (
                          <img
                            src={getProductImage(product.images)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black inline-block">
                            {product.product_code}
                          </span>
                          {renderProductTypeBadge(product.product_type)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {product.product_type === 'accessory' ? (
                      <div>{product.accessory_category_name || 'Accessory'}</div>
                    ) : (
                      <>
                        <div>{product.brand_name}</div>
                        <div className="text-xs text-gray-400">{product.size_dimension}</div>
                      </>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-accent-gold">
                    {formatCurrency(product.selling_price)}
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-semibold ${
                          product.stock_quantity > product.min_stock_level
                            ? 'text-green-600'
                            : product.stock_quantity > 0
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {product.stock_quantity} {product.unit}
                      </span>
                    </td>
                  )}
                  {canEdit && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/products/${product.id}/edit`);
                        }}
                        className="text-green-600 hover:text-green-900"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  // Compact View - Small grid with minimal info
  const renderCompactView = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
      {products.map((product) => (
        <div
          key={product.id}
          onClick={() => navigate(`/products/${product.id}`)}
          className={`rounded-lg shadow-md p-2 sm:p-3 hover:shadow-lg transition-shadow cursor-pointer min-h-[160px] flex flex-col ${
            product.stock_quantity <= product.min_stock_level
              ? 'bg-red-50 border-2 border-red-300'
              : 'bg-white'
          }`}
        >
          <div className="bg-gray-200 h-20 sm:h-24 rounded flex items-center justify-center mb-2 overflow-hidden">
            {getProductImage(product.images) ? (
              <img
                src={getProductImage(product.images)}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
            )}
          </div>
          <div className="text-[10px] sm:text-xs font-mono font-semibold px-1 sm:px-1.5 py-0.5 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black text-center mb-1 truncate">
            {product.product_code}
          </div>
          <h3 className="text-xs font-semibold text-primary-black line-clamp-2 mb-1 flex-grow">
            {product.name}
          </h3>
          <div className="space-y-0.5">
            <p className="text-xs sm:text-sm font-bold text-accent-gold truncate">
              {formatCurrency(product.selling_price)}
            </p>
            {canEdit && (
              <p
                className={`text-[10px] sm:text-xs font-semibold ${
                  product.stock_quantity > product.min_stock_level
                    ? 'text-green-600'
                    : product.stock_quantity > 0
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}
              >
                {product.stock_quantity} {product.unit}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Grid View - Current default view
  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {products.map((product) => (
        <div
          key={product.id}
          className={`rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
            product.stock_quantity <= product.min_stock_level
              ? 'bg-red-50 border-2 border-red-300'
              : 'bg-white'
          }`}
        >
          <div onClick={() => navigate(`/products/${product.id}`)}>
            <div className="bg-gray-200 h-48 flex items-center justify-center overflow-hidden">
              {getProductImage(product.images) ? (
                <img
                  src={getProductImage(product.images)}
                  alt={product.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <Package className="w-16 h-16 text-gray-400" />
              )}
            </div>
            <div className="p-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-mono font-semibold px-2 py-1 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black border border-accent-goldDark">
                  {product.product_code}
                </span>
                {renderProductTypeBadge(product.product_type)}
              </div>
              <h3 className="text-lg font-semibold text-primary-black mb-2 line-clamp-2">
                {product.name}
              </h3>
              <div className="space-y-1 mb-3 text-sm text-gray-600">
                {product.product_type === 'accessory' ? (
                  <>
                    <p>
                      <span className="font-medium">Category:</span>{' '}
                      {product.accessory_category_name || 'N/A'}
                    </p>
                    <p>
                      <span className="font-medium">Unit:</span> {product.unit || 'items'}
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      <span className="font-medium">Brand:</span> {product.brand_name}
                    </p>
                    <p>
                      <span className="font-medium">Size:</span> {product.size_dimension}
                    </p>
                    <p>
                      <span className="font-medium">Type:</span> {product.tile_type_name}
                    </p>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between mb-3 pb-3 border-b">
                <div>
                  <p className="text-xs text-gray-500">Price</p>
                  <p className="text-lg font-bold text-accent-gold">
                    {formatCurrency(product.selling_price)}
                  </p>
                </div>
                {canEdit && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Stock</p>
                    <p
                      className={`text-lg font-bold ${
                        product.stock_quantity > product.min_stock_level
                          ? 'text-green-600'
                          : product.stock_quantity > 0
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {product.stock_quantity} {product.unit}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Detailed View - Large cards with all information
  const renderDetailedView = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
      {products.map((product) => (
        <div
          key={product.id}
          className={`rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
            product.stock_quantity <= product.min_stock_level
              ? 'bg-red-50 border-2 border-red-300'
              : 'bg-white'
          }`}
        >
          <div className="sm:flex">
            <div
              onClick={() => navigate(`/products/${product.id}`)}
              className="sm:w-1/3 bg-gray-200 h-40 sm:h-auto flex items-center justify-center overflow-hidden"
            >
              {getProductImage(product.images) ? (
                <img
                  src={getProductImage(product.images)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Package className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
              )}
            </div>
            <div className="p-3 sm:p-4 sm:w-2/3">
              <div onClick={() => navigate(`/products/${product.id}`)}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono font-semibold px-2 py-1 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black inline-block mb-2">
                      {product.product_code}
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-primary-black mb-1 line-clamp-2">
                      {product.name}
                    </h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 text-xs sm:text-sm">
                  <div>
                    <p className="text-gray-500 text-[10px] sm:text-xs">Brand</p>
                    <p className="font-semibold text-gray-900 truncate">{product.brand_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[10px] sm:text-xs">Size</p>
                    <p className="font-semibold text-gray-900 truncate">{product.size_dimension}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[10px] sm:text-xs">Type</p>
                    <p className="font-semibold text-gray-900 truncate">{product.tile_type_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[10px] sm:text-xs">Use Case</p>
                    <p className="font-semibold text-gray-900 truncate">
                      {product.use_case_name || '-'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 pb-3 border-t border-b py-2 sm:py-3">
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500">Selling</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-accent-gold truncate">
                      {formatCurrency(product.selling_price)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500">Cost</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-700 truncate">
                      {formatCurrency(product.cost_price)}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="text-right">
                      <p className="text-[10px] sm:text-xs text-gray-500">Stock</p>
                      <p
                        className={`text-base sm:text-lg md:text-xl font-bold ${
                          product.stock_quantity > product.min_stock_level
                            ? 'text-green-600'
                            : product.stock_quantity > 0
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {product.stock_quantity}
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-500">{product.unit}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black">
                Product Management
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">Manage your tile inventory</p>
            </div>
            {canEdit && (
              <button
                onClick={() => navigate('/products/new')}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors min-h-[44px]"
              >
                <Plus className="w-5 h-5" />
                Add Product
              </button>
            )}
          </div>

          {/* Statistics */}
          {canEdit && statistics && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4 border border-blue-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                  Total Products
                </h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.total_products}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-green-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Total Stock</h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.total_stock}
                </p>
              </div>
              <div
                onClick={() => {
                  handleFilterChange('lowStockOnly', !filters.lowStockOnly);
                  if (!filters.lowStockOnly) {
                    setShowFilters(true);
                  }
                }}
                className="bg-white rounded-lg shadow-md p-4 border border-yellow-500 cursor-pointer hover:shadow-lg transition-shadow"
                title="Click to filter low stock products"
              >
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Low Stock</h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.low_stock_products}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-purple-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Stock Cost</h3>
                <p className="text-base md:text-lg lg:text-xl font-bold text-primary-black">
                  <BlurredValue value={formatCurrency(statistics.total_stock_cost || 0)} />
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-accent-gold">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Stock Value</h3>
                <p className="text-base md:text-lg lg:text-xl font-bold text-primary-black">
                  <BlurredValue value={formatCurrency(statistics.total_stock_value || 0)} />
                </p>
              </div>
            </div>
          )}

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                {/* View Mode Switcher - Now visible on mobile */}
                <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1 border border-gray-300 rounded-lg bg-gray-50">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'list'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="List View"
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('compact')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'compact'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Compact View"
                  >
                    <Grid2x2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'grid'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Grid View"
                  >
                    <LayoutGrid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('detailed')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'detailed'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Detailed View"
                  >
                    <Grid3x3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px]"
                >
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Filters</span>
                </button>
                <button
                  onClick={() => {
                    fetchProducts();
                    fetchStatistics();
                  }}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px]"
                >
                  <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  {/* Branch Filter - Only in multi-branch mode */}
                  {isMultiBranchMode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          Branch
                        </span>
                      </label>
                      <select
                        value={selectedBranchId}
                        onChange={(e) => setSelectedBranchId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                      >
                        <option value="">All Branches (Global)</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Product Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Type
                    </label>
                    <select
                      value={filters.productType}
                      onChange={(e) => handleFilterChange('productType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Products</option>
                      <option value="tile">Tiles Only</option>
                      <option value="accessory">Accessories Only</option>
                    </select>
                  </div>

                  {/* Brand Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                    <select
                      value={filters.brandId}
                      onChange={(e) => handleFilterChange('brandId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Brands</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Size Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <select
                      value={filters.sizeId}
                      onChange={(e) => handleFilterChange('sizeId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Sizes</option>
                      {sizes.map((size) => (
                        <option key={size.id} value={size.id}>
                          {size.dimension}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tile Type
                    </label>
                    <select
                      value={filters.typeId}
                      onChange={(e) => handleFilterChange('typeId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Types</option>
                      {tileTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filters.inStock}
                      onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                      className="rounded text-accent-gold focus:ring-accent-gold"
                    />
                    <span className="text-sm text-gray-700">In Stock Only</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filters.lowStockOnly}
                      onChange={(e) => handleFilterChange('lowStockOnly', e.target.checked)}
                      className="rounded text-accent-gold focus:ring-accent-gold"
                    />
                    <span className="text-sm text-gray-700">Low Stock Only</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filters.isFeatured === true}
                      onChange={(e) =>
                        handleFilterChange('isFeatured', e.target.checked ? true : '')
                      }
                      className="rounded text-accent-gold focus:ring-accent-gold"
                    />
                    <span className="text-sm text-gray-700">Featured Only</span>
                  </label>

                  <button
                    onClick={clearFilters}
                    className="ml-auto flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                  >
                    <X className="w-4 h-4" />
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Products Found</h3>
            <p className="text-gray-500 mb-6">
              {filters.search || filters.brandId || filters.sizeId || filters.typeId
                ? 'Try adjusting your filters'
                : 'Get started by adding your first product'}
            </p>
            {canEdit && (
              <button
                onClick={() => navigate('/products/new')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Product
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Render products based on selected view mode */}
            {renderProducts()}

            {/* Pagination */}
            <div className="mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={filters.limit}
                onPageChange={(page) => handleFilterChange('page', page)}
                itemLabel="products"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
